import { useTheme } from "@emotion/react";
import { TextField, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginApi, resendOtpApi, verifyOtpApi } from "../../../API/auth";
import { loginSuccess as loginAction } from "../../../store/actions/auth";
import { errorResponseHandler } from "../../../utils/reposeAlert";
import LoginButton from "../../Login/LoginButton";
import LoginInput from "../../Login/LoginInput";
import LoginLogos from "../../Login/LoginLogos";
import  '../../../theme/style.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    profileId: "",
    password: "",
  });

  // MFA / OTP local state — tokens are NEVER stored until verifyOtp succeeds.
  const [step, setStep] = useState("credentials");
  const [mfaToken, setMfaToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);

  const fields = [
    {
      name: "profileId",
      label: "Profile ID",
      placeholder: "Enter your Profile ID",
      type: "text",
    },
    {
      name: "password",
      label: "Password",
      placeholder: "Enter your Password",
      type: "password",
    },
  ];

  // Resend cooldown countdown.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Focus the first OTP box when entering the OTP step.
  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const loginData = {
      profileId: formData.profileId,
      password: formData.password,
    };
    setLoading(true);
    await loginApi(loginData)
      .then((response) => {
        if (response.data.mfaRequired === true) {
          // MFA required: switch to the OTP step. DO NOT store tokens or navigate.
          setMfaToken(response.data.mfaToken);
          setMaskedEmail(response.data.email);
          setOtp("");
          setOtpError("");
          setResendCooldown(RESEND_COOLDOWN);
          setStep("otp");
          return;
        }
        // Backend returned tokens directly (not expected for web, but handle it).
        const { access, refresh } = response.data;
        dispatch(
          loginAction({
            accessToken: access.token,
            refreshToken: refresh.token,
          })
        );
        setFormData({
          profileId: "",
          password: "",
        });
        navigate("/");
      })
      .catch(() => {
        // Never surface the backend message here: it can reveal whether an
        // account exists. Always show the same generic failure text.
        errorResponseHandler("Login Failed", "Invalid profile ID or password.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const setOtpDigit = (index, digit) => {
    setOtp((prev) => {
      const chars = prev.padEnd(OTP_LENGTH, " ").split("");
      chars[index] = digit || " ";
      return chars.join("").replace(/\s+$/, "");
    });
  };

  const otpChangeHandler = (index, event) => {
    const raw = event.target.value.replace(/\D/g, "");
    if (!raw) {
      setOtpDigit(index, "");
      return;
    }
    // Take the last entered digit (handles overwrite of an existing value).
    const digit = raw[raw.length - 1];
    setOtpDigit(index, digit);
    setOtpError("");
    if (index < OTP_LENGTH - 1 && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  const otpKeyDownHandler = (index, event) => {
    if (event.key === "Backspace") {
      if (!otp[index] && index > 0 && otpRefs.current[index - 1]) {
        event.preventDefault();
        setOtpDigit(index - 1, "");
        otpRefs.current[index - 1].focus();
      } else {
        setOtpDigit(index, "");
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const otpPasteHandler = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    setOtp(pasted);
    setOtpError("");
    const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[nextIndex]?.focus();
  };

  const verifyHandler = async (e) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    setVerifyLoading(true);
    setOtpError("");
    await verifyOtpApi({ mfaToken, otp })
      .then((res) => {
        // deviceToken in the response is ignored on web.
        const { access, refresh } = res.data;
        dispatch(
          loginAction({
            accessToken: access.token,
            refreshToken: refresh.token,
          })
        );
        setFormData({
          profileId: "",
          password: "",
        });
        navigate("/");
      })
      .catch((err) => {
        setOtpError(err.response?.data?.message || "Invalid code");
      })
      .finally(() => {
        setVerifyLoading(false);
      });
  };

  const resendHandler = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setOtpError("");
    await resendOtpApi({ mfaToken })
      .then((res) => {
        setMfaToken(res.data.mfaToken);
        setMaskedEmail(res.data.email);
        setOtp("");
        setResendCooldown(RESEND_COOLDOWN);
        if (otpRefs.current[0]) otpRefs.current[0].focus();
      })
      .catch((err) => {
        setOtpError(
          err.response?.data?.message ||
            "Could not resend the code. Please try again."
        );
      })
      .finally(() => {
        setResendLoading(false);
      });
  };

  const backToLogin = () => {
    setStep("credentials");
    setMfaToken("");
    setMaskedEmail("");
    setOtp("");
    setOtpError("");
    setResendCooldown(0);
  };

  return (
    <div  className="login-bg">
      <div className="logo-form">
     <LoginLogos/>
     <div className="form-side">
      {step === "credentials" ? (
      <form onSubmit={submitHandler}>
            <Stack

            >
              <Stack
                sx={{
               display:"flex",
                 gap:"30px",
                mx: "auto",
                width:"100%"
              }}
              >
                <Stack
                >
                  <Box>
                    <Typography
                      variant="h4"
                      component="h4"
                      sx={{
                        textAlign: "center",
                        // color: theme.palette.white.main,
                        fontWeight: "bold",
                        color:theme.palette.secondary.main,
                        fontSize:"1.4rem"

                      }}
                    >
                      LOGIN
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="column" sx={{gap:"40px"}}>
                  {fields.map((field) => (
                    <LoginInput
                      key={field.name}
                      name={field.name}
                      label={field.label}
                      placeholder={field.placeholder}
                      value={formData[field.name]}
                      onChange={changeHandler}
                      type={field.type}
                    />

                  ))}
                </Stack>
                <div style={{fontSize:14}}>* The initial password is your registered email address</div>
                <Typography
                  onClick={() => navigate("/forgot-password")}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textAlign: "right",
                    marginTop: "-20px",
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Forgot password?
                </Typography>
                <LoginButton loading={loading} />
              </Stack>
            </Stack>
          </form>
      ) : (
          <form onSubmit={verifyHandler}>
            <Stack sx={{ display: "flex", gap: "30px", mx: "auto", width: "100%" }}>
              <Box>
                <Typography
                  variant="h4"
                  component="h4"
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    color: theme.palette.secondary.main,
                    fontSize: "1.4rem",
                  }}
                >
                  VERIFY YOUR IDENTITY
                </Typography>
                <Typography
                  sx={{
                    textAlign: "center",
                    color: theme.palette.gray.main,
                    fontSize: "14px",
                    marginTop: "12px",
                  }}
                >
                  We sent a 6-digit code to {maskedEmail}
                </Typography>
              </Box>

              <Stack
                direction="row"
                sx={{ gap: "10px", justifyContent: "center" }}
                onPaste={otpPasteHandler}
              >
                {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (otpRefs.current[index] = el)}
                    value={otp[index] || ""}
                    onChange={(e) => otpChangeHandler(index, e)}
                    onKeyDown={(e) => otpKeyDownHandler(index, e)}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 1,
                      "aria-label": `Digit ${index + 1}`,
                      style: {
                        textAlign: "center",
                        fontSize: "22px",
                        fontWeight: 600,
                        padding: "12px 0",
                        color: theme.palette.secondary.main,
                      },
                    }}
                    sx={{
                      width: "48px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: otpError
                            ? theme.palette.error.main
                            : theme.palette.third.main,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                ))}
              </Stack>

              {otpError ? (
                <Typography
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: "14px",
                    textAlign: "center",
                    marginTop: "-10px",
                  }}
                >
                  {otpError}
                </Typography>
              ) : null}

              <LoginButton loading={verifyLoading} text="Verify" />

              <Stack
                direction="row"
                sx={{ justifyContent: "center", alignItems: "center", gap: "6px" }}
              >
                <Typography sx={{ fontSize: "14px", color: theme.palette.gray.main }}>
                  Didn't receive the code?
                </Typography>
                <Typography
                  onClick={resendHandler}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color:
                      resendCooldown > 0 || resendLoading
                        ? theme.palette.gray.main
                        : theme.palette.primary.main,
                    cursor:
                      resendCooldown > 0 || resendLoading ? "default" : "pointer",
                    userSelect: "none",
                  }}
                >
                  {resendLoading
                    ? "Sending..."
                    : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
                </Typography>
              </Stack>

              <Typography
                onClick={backToLogin}
                sx={{
                  fontSize: "14px",
                  textAlign: "center",
                  color: theme.palette.secondary.main,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                Back to login
              </Typography>
            </Stack>
          </form>
      )}
          </div>
          </div>
    </div>

  );
};

export default Login;
